import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAssignKioskStore } from '../../stores/assign-kiosk-store';
import { EvKioskScreen } from '../organisms/EvKioskScreen';
import { OcpiScreen } from '../organisms/OcpiScreen';
import { KioskSettingsPanel } from '../organisms/KioskSettingsPanel';
import { appConfig } from '@/core/config';

type Tab = 'assign' | 'settings';

export function AssignKioskPage() {
  const { machineId: routeMachineId } = useParams<{ machineId: string }>();
  const [searchParams] = useSearchParams();
  const DEFAULT_MACHINE_ID = appConfig.getEnvironment() === 'prod' ? '485182134' : '1000403131';
  const urlMachineId = parseInt(routeMachineId || '', 10);
  const machineId = !isNaN(urlMachineId)
    ? String(urlMachineId)
    : (searchParams.get('machineId') ?? DEFAULT_MACHINE_ID);

  const { screenType, isLoadingInit, isLoadingLocations, locationsFetched, initError, initialize, reset } = useAssignKioskStore();
  const [activeTab, setActiveTab] = useState<Tab>('assign');

  useEffect(() => {
    initialize(machineId);
    return () => { reset(); };
  }, [machineId]);

  // ── Loading — keep spinner until both init AND initial location fetch complete ──
  if (isLoadingInit || (isLoadingLocations && !locationsFetched)) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-yellow-400" />
          <p className="text-sm font-medium text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  // ── Configuration incomplete (UNKNOWN machine model) ──────────────────────
  if (screenType === 'FALLBACK') {
    return (
      <div className="flex h-screen flex-col bg-white">
        <div className="bg-white ml-4 pl-4 pr-6 py-4 shadow-sm relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 bg-yellow-400" />
          <div className="flex items-baseline gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Assign Kiosk</h1>
            {machineId && <p className="text-sm text-gray-500">ID: {machineId}</p>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-100">
          <div className="p-3 sm:p-6">
            <div className="flex min-h-[32rem] items-center justify-center rounded-lg border border-gray-200 bg-white">
              <div>
                <p className="mb-3 text-sm text-gray-400">
                  ⚠️ Configuration incomplete. The following required field(s) are missing:
                </p>
                <ul className="space-y-1 text-sm text-gray-400">
                  <li>• Machine Type</li>
                  <li>• Machine Protocol</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Fatal error ────────────────────────────────────────────────────────────
  if (initError) {
    return (
      <div className="flex h-screen flex-col bg-white">
        <div className="flex-1 overflow-y-auto bg-gray-100">
          <div className="p-3 sm:p-6">
            <div className="flex min-h-[32rem] items-center justify-center rounded-lg border border-gray-200 bg-white">
              <div>
                <p className="text-base text-gray-400">
                  ⚠️ Machine not found.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      {/* Header + Tabs */}
      <div className="bg-white pl-4 pr-6 shadow-sm relative border-b-2 border-gray-200">
        <div className="flex items-center gap-6">
          {(screenType === 'EV_KIOSK' || screenType === 'UNKNOWN') && (
            <div className="flex gap-4 sm:gap-8">
              <button
                onClick={() => setActiveTab('assign')}
                className={`py-4 px-2 text-sm sm:text-base transition-all border-b-4 ${
                  activeTab === 'assign'
                    ? 'border-yellow-400 text-gray-900 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Assign Location
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-4 px-2 text-sm sm:text-base transition-all border-b-4 ${
                  activeTab === 'settings'
                    ? 'border-yellow-400 text-gray-900 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Kiosk Settings
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'assign' ? (
        <div className="flex-1 overflow-y-auto bg-gray-100">
          <div className="p-3 sm:p-6">
            {screenType === 'OCPI' ? <OcpiScreen /> : <EvKioskScreen />}
          </div>
        </div>
      ) : (screenType === 'EV_KIOSK' || screenType === 'UNKNOWN') && (
        <div className="flex-1 overflow-y-auto bg-gray-100">
          <div className="p-3 sm:p-6">
            <KioskSettingsPanel />
          </div>
        </div>
      )}
    </div>
  );
}
